import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import jsPDF from 'jspdf';

export function SearchResults({ searchResults, searchQuery, onNewSearch, onRequestApp }) {
  if (!searchResults) return null;

  const contentRef = useRef(null);

  const handleExportPDF = () => {
    try {
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Set default font
      pdf.setFont('helvetica', 'normal');
      
      // Add header with gradient-like effect
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, 210, 40, 'F'); // Increased height for more content
      
      // Add title and search query
      pdf.setFontSize(24);
      pdf.setTextColor(255, 255, 255);
      pdf.text('App Search Results', 20, 20);
      
      // Add search query
      pdf.setFontSize(14);
      pdf.text('Search Query:', 20, 30);
      pdf.setFontSize(12);
      pdf.text(`"${searchQuery || 'No search query provided'}"`, 20, 37);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Process the markdown content
      const content = searchResults.searchResults;
      const lines = content.split('\n');
      
      let y = 50; // Start content below header
      const margin = 20;
      const pageWidth = 170; // A4 width - margins
      
      // Helper function to clean URL
      const cleanUrl = (url) => {
        return url.replace(/[),.]+$/, ''); // Remove trailing ), . etc.
      };
      
      // Helper function to add text with proper wrapping
      const addText = (text, fontSize = 12, isBold = false, isLink = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        
        // Handle markdown formatting
        text = text.replace(/\*\*(.*?)\*\*/g, '$1'); // Remove ** for bold
        text = text.replace(/\*(.*?)\*/g, '$1'); // Remove * for italic
        
        const splitText = pdf.splitTextToSize(text, pageWidth);
        
        splitText.forEach(line => {
          if (y > 270) { // Check if we need a new page
            pdf.addPage();
            y = 20;
          }
          
          if (isLink) {
            // Add link with blue color and underline
            pdf.setTextColor(0, 0, 255);
            pdf.textWithLink(line, margin, y, {
              url: cleanUrl(line)
            });
          } else {
            pdf.setTextColor(0, 0, 0);
            pdf.text(line, margin, y);
          }
          
          y += fontSize * 0.5; // Adjust line height based on font size
        });
        
        y += 5; // Add some space after each block
      };

      // Helper function to add match classification
      const addMatchClassification = (classification) => {
        const rating = classification.split(':')[1]?.trim() || 'NONE';
        const ratingColors = {
          'EXCELLENT': [34, 197, 94], // Green
          'GOOD': [59, 130, 246],     // Blue
          'FAIR': [234, 179, 8],      // Yellow
          'POOR': [249, 115, 22],     // Orange
          'NONE': [239, 68, 68]       // Red
        };
        
        const [r, g, b] = ratingColors[rating] || [100, 100, 100];
        
        // Add colored box
        pdf.setFillColor(r, g, b);
        pdf.roundedRect(margin, y, 40, 10, 2, 2, 'F');
        
        // Add text
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.text(rating, margin + 20, y + 6, { align: 'center' });
        
        y += 15; // Add space after classification
      };

      // Process each line of content
      lines.forEach(line => {
        if (line.trim() === '') {
          y += 5; // Add space for empty lines
          return;
        }

        // Handle match classification
        if (line.includes('MATCH_CLASSIFICATION:')) {
          addMatchClassification(line);
          return;
        }

        // Handle headers
        if (line.startsWith('# ')) {
          addText(line.substring(2), 20, true);
        } else if (line.startsWith('## ')) {
          addText(line.substring(3), 16, true);
        } else if (line.startsWith('### ')) {
          addText(line.substring(4), 14, true);
        }
        // Handle app listings
        else if (line.includes('App Name:')) {
          // Add a separator
          pdf.setDrawColor(200, 200, 200);
          pdf.line(margin, y, margin + pageWidth, y);
          y += 10;
          
          // Add app name in bold
          const appName = line.split('App Name:')[1].trim();
          addText(`App: ${appName}`, 14, true);
        }
        // Handle match ratings
        else if (line.includes('Match rating:')) {
          const rating = line.split('Match rating:')[1].trim();
          addText(`Match Rating: ${rating}`, 12, true);
        }
        // Handle links
        else if (line.includes('http')) {
          const url = line.match(/(https?:\/\/[^\s]+)/)?.[0];
          if (url) {
            addText(cleanUrl(url), 12, false, true);
          } else {
            addText(line);
          }
        }
        // Handle other content
        else {
          addText(line);
        }
      });

      // Add footer with page numbers and attribution
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        
        // Add page number
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${i} of ${pageCount}`,
          pdf.internal.pageSize.width / 2,
          pdf.internal.pageSize.height - 10,
          { align: 'center' }
        );

        // Add attribution
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          'Generated by Zaptopia.ai',
          pdf.internal.pageSize.width / 2,
          pdf.internal.pageSize.height - 5,
          { align: 'center' }
        );
      }

      // Save the PDF
      pdf.save('app-search-results.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
      <div className="max-w-6xl mx-auto">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 rounded-3xl blur-lg opacity-20"></div>
          <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl p-4 sm:p-8 lg:p-12 shadow-2xl border border-white/50">
            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3 sm:ml-4">
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Search Results</h2>
                  <p className="text-sm sm:text-base text-gray-600 mt-1">Found solutions for your request</p>
                </div>
              </div>
            </div>
            
            {/* Results Content */}
            <div className="max-w-none mb-10">
              <div className="prose prose-lg max-w-none">
                <ReactMarkdown 
                  ref={contentRef}
                  className="search-results-content"
                  components={{
                    h1: ({children}) => (
                      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-10 sm:mt-12 mb-6 sm:mb-8 flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        {children}
                      </h1>
                    ),
                    h2: ({children}) => (
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mt-8 sm:mt-10 mb-4 sm:mb-6 flex items-center">
                        <div className="w-6 h-6 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        {children}
                      </h2>
                    ),
                    h3: ({children}) => (
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mt-6 sm:mt-8 mb-3 sm:mb-4 flex items-center">
                        <div className="w-5 h-5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-2">
                          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        {children}
                      </h3>
                    ),
                    p: ({children}) => {
                      // Regular paragraph styling
                      return (
                        <p className="mb-4 text-gray-700 leading-relaxed bg-gray-50/50 p-4 rounded-xl border border-gray-100 text-sm sm:text-base">
                          {children}
                        </p>
                      );
                    },
                    ul: ({children}) => (
                      <ul className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                        {children}
                      </ul>
                    ),
                    ol: ({children}) => (
                      <ol className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                        {children}
                      </ol>
                    ),
                    li: ({children}) => {
                      // Check if this is an app listing
                      if (typeof children === 'string' && children.includes('Name:')) {
                        const appInfo = children.split('\n').reduce((acc, line) => {
                          const [key, value] = line.split(':').map(s => s.trim());
                          if (key && value) {
                            if (key === 'Links') {
                              // Parse markdown links
                              const linksText = value.trim();
                              // Split by newline and filter out empty lines
                              const linkLines = linksText.split('\n').filter(line => line.trim());
                              const links = linkLines.map(link => {
                                // Handle markdown format [text](url)
                                const markdownMatch = link.match(/\[([^\]]+)\]\(([^)]+)\)/);
                                if (markdownMatch) {
                                  return {
                                    type: markdownMatch[1],
                                    url: markdownMatch[2]
                                  };
                                }
                                // Handle simple format
                                const [type, url] = link.split(':').map(s => s.trim());
                                return { type, url };
                              }).filter(link => link.type && link.url);
                              acc[key] = links;
                            } else {
                              acc[key] = value;
                            }
                          }
                          return acc;
                        }, {});

                        // Debug log to check parsed links
                        console.log('Parsed app info:', appInfo);

                        return (
                          <li className="group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative p-4 sm:p-6">
                              {/* Header with Name and Match Rating */}
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
                                <h4 className="text-lg sm:text-xl font-bold text-gray-900">{appInfo['Name']}</h4>
                                {appInfo['Match rating'] && (
                                  <div className="flex items-center">
                                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                      {appInfo['Match rating'].split('/')[0]}
                                    </div>
                                    <span className="ml-2 text-sm text-gray-500">/5</span>
                                  </div>
                                )}
                              </div>

                              {/* Type and Platforms */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {appInfo['Type'] && (
                                  <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                                    {appInfo['Type']}
                                  </span>
                                )}
                                {appInfo['Platforms'] && (
                                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                    {appInfo['Platforms']}
                                  </span>
                                )}
                                {appInfo['Pricing'] && (
                                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                    {appInfo['Pricing']}
                                  </span>
                                )}
                              </div>

                              {/* Description */}
                              {appInfo['Description'] && (
                                <p className="text-gray-600 mb-4">{appInfo['Description']}</p>
                              )}

                              {/* Links */}
                              {appInfo['Links'] && appInfo['Links'].length > 0 && (
                                <div className="space-y-2 mb-4">
                                  <h5 className="text-sm font-medium text-gray-700">Available on:</h5>
                                  <div className="flex flex-wrap gap-2">
                                    {appInfo['Links'].map((link, index) => (
                                      <a
                                        key={index}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                                      >
                                        {link.type === 'Website' && (
                                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                          </svg>
                                        )}
                                        {link.type === 'GitHub' && (
                                          <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                          </svg>
                                        )}
                                        {link.type === 'App Store' && (
                                          <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                          </svg>
                                        )}
                                        {link.type === 'Play Store' && (
                                          <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M3.609 1.814L13.792 12 3.609 22.186c-.14-.14-.25-.32-.25-.52V2.334c0-.2.11-.38.25-.52zM14.5 12l10.5 6.13v-12.26L14.5 12zm-1.5 1.5L2.5 19.5v-15l10.5 6z"/>
                                          </svg>
                                        )}
                                        {link.type === 'Documentation' && (
                                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                        )}
                                        {link.type}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Status and Limitations */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                {appInfo['Status'] && (
                                  <div className="flex items-start">
                                    <span className="text-sm font-medium text-gray-500 min-w-[100px] sm:min-w-[120px]">Status:</span>
                                    <span className="text-sm text-gray-700 flex-1">{appInfo['Status']}</span>
                                  </div>
                                )}
                                {appInfo['Limitations'] && (
                                  <div className="flex items-start">
                                    <span className="text-sm font-medium text-gray-500 min-w-[100px] sm:min-w-[120px]">Limitations:</span>
                                    <span className="text-sm text-gray-700 flex-1">{appInfo['Limitations']}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      }
                      
                      // Regular list item styling
                      return (
                        <li className="text-gray-700 mb-2">{children}</li>
                      );
                    },
                    a: ({href, children}) => (
                      <a 
                        href={href} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center hover:underline text-sm sm:text-base"
                      >
                        {children}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ),
                    strong: ({children}) => {
                      // Regular strong styling
                      return (
                        <strong className="font-semibold text-gray-900 bg-yellow-50 px-1.5 py-0.5 rounded text-sm sm:text-base">
                          {children}
                        </strong>
                      );
                    },
                  }}
                >
                  {searchResults.searchResults}
                </ReactMarkdown>
              </div>
            </div>

            {/* Classification Display */}
            {searchResults.classification && searchResults.classification !== 'NOT_FOUND' && (
              <div className="mt-6 sm:mt-8 mb-6 sm:mb-8">
                <div className="flex justify-center">
                  {(() => {
                    const rating = searchResults.classification;
                    const ratingColor = {
                      'EXCELLENT': 'from-green-500 to-emerald-500',
                      'GOOD': 'from-blue-500 to-indigo-500',
                      'FAIR': 'from-yellow-500 to-amber-500',
                      'POOR': 'from-orange-500 to-red-500',
                      'NONE': 'from-red-500 to-pink-500'
                    }[rating] || 'from-gray-500 to-slate-500';

                    return (
                      <div className={`inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r ${ratingColor} text-white font-semibold text-base sm:text-lg shadow-lg`}>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {rating}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-8 space-y-4">
              <div className="text-sm text-gray-600 mb-6">
                {searchResults.hasGoodMatches ? (
                  <p>
                    We found some matching apps, but you can still submit a request if you'd like to see improvements or additional features.
                  </p>
                ) : (
                  <p>
                    No exact matches found. Submit a request to help us understand what you're looking for.
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <button
                  onClick={onRequestApp}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 text-white transition-all duration-300 hover:shadow-lg hover:shadow-indigo-500/25"
                >
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold">
                        Submit App Request
                      </h3>
                      <p className="mt-1 text-sm text-indigo-100">
                        Help us understand what you're looking for
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <svg className="h-6 w-6 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 opacity-0 transition-opacity group-hover:opacity-100"></div>
                </button>

                <button
                  onClick={handleExportPDF}
                  className="group relative overflow-hidden rounded-xl bg-white p-4 text-gray-900 transition-all duration-300 hover:bg-gray-50 hover:shadow-md border border-gray-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                        Export as PDF
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Download search results as a PDF
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                </button>

                <button
                  onClick={onNewSearch}
                  className="group relative overflow-hidden rounded-xl bg-white p-4 text-gray-900 transition-all duration-300 hover:bg-gray-50 hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                        New Search
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Try a different search query
                      </p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400 transform transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 